import DownloadIcon from '@material-design-icons/svg/filled/download.svg?react';
import StarIcon from '@material-design-icons/svg/filled/star.svg?react';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  getOrientation,
  type ImageDimensions,
} from '../../utils/imageDimensions';

interface DappPreviewShellProps {
  dappName: string;
  subtitle: string;
  description: string;
  bannerSlot?: ReactNode | null;
  iconSlot?: ReactNode | null;
  previewSlots: (ReactNode | null)[];
  previewDimensions?: ImageDimensions | null;
}

export function DappPreviewShell({
  dappName,
  subtitle,
  description,
  bannerSlot,
  iconSlot,
  previewSlots,
  previewDimensions,
}: DappPreviewShellProps) {
  const hasIcon = iconSlot !== null && iconSlot !== undefined;
  const previewOrientation = previewDimensions
    ? getOrientation(previewDimensions)
    : 'portrait';

  const previewSlotSizeClass = !previewDimensions
    ? 'w-44 h-80'
    : previewOrientation === 'landscape'
      ? 'w-full'
      : previewOrientation === 'square'
        ? 'w-56'
        : 'w-44';
  const previewSlotStyle = previewDimensions
    ? {
        aspectRatio: `${previewDimensions.width} / ${previewDimensions.height}`,
      }
    : undefined;

  return (
    <div className="sticky top-8">
      <div className="text-white text-lg font-medium mb-4 text-center">
        Preview
      </div>
      <div className="w-72 h-[667.50px] relative bg-white rounded-[30px] overflow-hidden mx-auto scale-90 origin-top">
        <div className="w-72 h-36 left-0 top-0 absolute bg-zinc-200">
          {bannerSlot}
        </div>

        <div className="size-24 left-[15px] top-[120px] absolute">
          <div
            className={twMerge(
              'w-24 h-24 left-0 top-0 absolute rounded-3xl shadow-[inset_0px_1.40625px_1.40625px_0px_rgba(255,255,255,0.17)]',
              hasIcon ? 'bg-transparent' : 'bg-slate-300',
            )}
          >
            {iconSlot}
          </div>
        </div>
        <div className="px-3 py-1.5 left-[190px] top-[180px] absolute bg-gray-950 rounded-3xl inline-flex justify-center items-center gap-1.5">
          <div className="size-4 relative">
            <DownloadIcon className="text-white fill-white w-full h-full" />
          </div>
          <div className="flex justify-center items-center">
            <div className="text-center text-white text-xs font-medium leading-normal">
              Install
            </div>
          </div>
        </div>

        <div
          className={twMerge(
            'w-40 h-6 left-[15px] top-[222px] absolute',
            dappName
              ? 'text-zinc-900 text-lg font-medium truncate'
              : 'bg-zinc-200 rounded',
          )}
        >
          {dappName || ''}
        </div>
        <div
          className={twMerge(
            'w-52 h-4 left-[15px] top-[250px] absolute',
            subtitle ? 'text-zinc-500 text-sm truncate' : 'bg-zinc-200 rounded',
          )}
        >
          {subtitle || ''}
        </div>

        <div className="left-[15px] top-[274.50px] absolute inline-flex justify-start items-center gap-1.5">
          <div className="text-center justify-center text-zinc-500 text-sm font-medium leading-3">
            5.0
          </div>
          <div className="w-12 flex justify-start items-center gap-[0.75px] text-zinc-500">
            {[...Array(5)].map((_, i) => (
              <StarIcon key={i} className="size-3" />
            ))}
          </div>
        </div>

        <div
          className={twMerge(
            'left-[15px] absolute',
            'top-[334px]',
            description ? 'text-zinc-700 text-xs leading-snug' : '',
          )}
        >
          {description ? (
            <p className="line-clamp-4 whitespace-pre-wrap">{description}</p>
          ) : (
            <>
              <div className="w-64 h-3 mb-2 bg-zinc-200 rounded" />
              <div className="w-64 h-3 mb-2 bg-zinc-200 rounded" />
              <div className="w-60 h-3 mb-2 bg-zinc-200 rounded" />
              <div className="w-28 h-3 bg-zinc-200 rounded" />
            </>
          )}
        </div>

        <div
          className={twMerge(
            'w-72 px-3 left-0 absolute inline-flex justify-start items-center gap-2 overflow-hidden',
            'top-[428.50px]',
          )}
        >
          {previewSlots.map((previewSlot, index) => (
            <div
              key={index}
              style={previewSlotStyle}
              className={twMerge(
                'shrink-0 rounded-2xl',
                previewSlotSizeClass,
                previewSlot ? 'bg-transparent' : 'bg-zinc-200',
              )}
            >
              {previewSlot}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
